/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { ContractPeriodType, ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP, ElectricityAgreementForm, ElectricityAgreementPdfPreviewRequest, ElectricityPackage, ElectricityPackageBaseFixationType, ElectricityPackageEarlyTerminationFeeType } from "models/electricity/new-electricity-agreement";
import NewElectricityAgreementCustomerForm from "./NewElectricityAgreementCustomerForm";
import { FormContentBlock } from "../../common/FormContentBlock";
import { Button, Form, Spinner } from "react-bootstrap";
import { MeteringPoint } from "models/electricity/metering-point";
import moment from "moment";
import DatePicker from "react-date-picker";
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { Document, Page, pdfjs } from "react-pdf";
import ElectricityAgreementService from "services/electricity/electricity-agreement.service";
import { AuthenticationContext } from "services/context/authentication.context";
import { dateFormat, isoDateFormat } from "common/dateFormatterConfig";
import { blobToDataURL, blobToObjectURL } from "common/file-util";
import { getPackagePrice } from "helpers/electricity-helper";
import { roundNumber, getPriceWithVAT } from "helpers";
import printJS from "print-js";
import PDFPreviewModal from "components/Modals/PDFPreviewModal/PDFPreviewModal";
import NewElectricityAgreementBillingForm from "./NewElectricityAgreementBillingForm";
import { getBaseConsumptionProportionScale } from "components/Agreements/ElectricityAgreementCard";
import { isNotNullOrUndefined } from "common/data-util";
import { ElectricityAgreement } from "models/electricity/agreement";
import { SUM_ROUNDING_SCALE } from "common/constants";

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const MAX_DATE_DAYS = 60;

interface NewElectricityAgreementSummaryProps { 
    form: ElectricityAgreementForm;
    setForm: any;
    isCompany: boolean;
    selectedPackage: ElectricityPackage;
    selectedMeteringPoints: MeteringPoint[];
    onChangePackage: () => void;
    toggleTerms: () => void;
    termsAccepted: boolean;
    productionPackage?: ElectricityPackage;
    offer?: ElectricityAgreement;
 }

export function NewElectricityAgreementSummary({ 
    form, 
    setForm, 
    isCompany, 
    selectedPackage, 
    selectedMeteringPoints, 
    onChangePackage, 
    toggleTerms, 
    termsAccepted,
    productionPackage,
    offer
}: NewElectricityAgreementSummaryProps
) {

    const [earliestPossibleAgreementStartDate, setEarliestPossibleAgreementStartDate] = useState<any>();

    const [pdfPreview, setPdfPreview] = useState<string | undefined>();
    const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
    const [pdfPreviewIsDownloading, setPdfPreviewIsDownloading] = useState(false);

    const [numPages, setNumPages] = useState<number | undefined>();
    const [termsPdfPreview, setTermsPdfPreview] = useState<string | undefined>();
    const [termsPdfLoaded, setTermsPdfLoaded] = useState(false);
    const [clientWidth, setClientWidth] = useState(window.innerWidth);
    
    const possibleDatesForPackageChange = [
        moment().add(1, 'month').startOf('month'),
        moment().add(2, 'month').startOf('month')
    ];

    const { state } = useContext(AuthenticationContext);
    const isExistingAgreementStartInFuture = !!state.changePackageAgreement && moment(state.changePackageAgreement.startDate).isAfter(moment());

    const withResale = (form.sellCheck && !!productionPackage) 
        || !!state.changePackageAgreement?.productionPackage
        || !!offer?.productionPackage;

    useEffect(() => {
        const currentDate = form.startDate ? moment(form.startDate) : null;
        let earliestDate;
        if (!state.changePackageAgreement) {
            earliestDate = [...selectedMeteringPoints].sort((a, b) => b.earliestPossibleAgreementStartDate.localeCompare(a.earliestPossibleAgreementStartDate))[0].earliestPossibleAgreementStartDate;
        } else {
            earliestDate = possibleDatesForPackageChange[0];
        }
        const earliestDateObj = moment(earliestDate);
        let startDate = currentDate && currentDate.isAfter(earliestDate) ? currentDate.toDate() : earliestDateObj.toDate();
        if (state.changePackageAgreement?.startDate && moment(state.changePackageAgreement.startDate).isAfter(moment())) {
            startDate = moment(state.changePackageAgreement.startDate).toDate()
        }

        setEarliestPossibleAgreementStartDate(earliestDateObj.toDate());
        setForm({
            ...form,
            startDate
        });
    }, []);

    const generateFilename = () => {
        const contractNumber = state.changePackageAgreement?.contractNumber;
        return contractNumber 
            ? contractNumber 
            : `${moment().format("YYYYMMDD")}-${state.customerId!}`;
    };

    const getPdfPreview = async (
        withDownload?: boolean
    ) => {
        try {
            if (withDownload) {
                setPdfPreviewIsDownloading(true)      
            } else {
                setPdfPreviewLoading(true);
            }

            let startDate = moment(form.startDate!).format(isoDateFormat);
            if(agreementStartDate) {
                startDate = moment(agreementStartDate).format(isoDateFormat);
            }

            const request : ElectricityAgreementPdfPreviewRequest = {
                contractNumber: isExistingAgreementStartInFuture ? state.changePackageAgreement!.contractNumber : undefined,
                customerId: state.customerId!, 
                contactPersonId: state.customer!.contactId,
                meteringPointIds: selectedMeteringPoints.map(mp => mp.id), 
                startDate: startDate, 
                endDate: agreementEndDate,
                consumptionPackageId: selectedPackage.id,
                productionPackageId: withResale ? productionPackage!.id : undefined,
                consumptionMarginal: consumptionMarginal, 
                productionMarginal: withResale ? productionMarginal : undefined,
                basePriceCentsKwh: basePriceCentsKwh,
                baseConsumptionProportion: baseConsumptionProportion,
                dayTariffCentsKwh: dayTariffCentsKwh,
                nightTariffCentsKwh: nightTariffCentsKwh,
                monthlyFeeEuros: monthlyFeeEuros,
                earlyTerminationFee: earlyTerminationFee,
                estimatedConsumptionVolumeMwh: offer?.estimatedConsumptionVolumeMwh,
                estimatedConsumptionDayVolumeMwh: offer?.estimatedConsumptionDayVolumeMwh,
                estimatedConsumptionNightVolumeMwh: offer?.estimatedConsumptionNightVolumeMwh,
                email: form.email!, 
                phone: form.phone!, 
                address: form.address!,
                contractPeriodId: offer?.contractPeriod?.id || selectedPackage.contractPeriod?.id,
            };

            if (withDownload) {
                await ElectricityAgreementService.getElectricityAgreementPdfPreviewWithDownload(request, generateFilename());
            } else {
                const blob = await ElectricityAgreementService.getElectricityAgreementPdfPreview(request);    
                setPdfPreview(blobToObjectURL(blob));
            }
        } catch (e) {
            console.error(e)
        } finally {
            setPdfPreviewLoading(false);
            setPdfPreviewIsDownloading(false)
        }
    }

    useEffect(() => {
        getTermsPdfPreview();
    }, []);

    const getTermsPdfPreview = async () => {
        const blob = await ElectricityAgreementService.getElectricityAgreementTermsPdf(selectedPackage.id, isCompany);

        blobToDataURL(blob).then(res => {
            setTermsPdfPreview(res);
        })
    }

    function onDocumentLoadSuccess({ numPages } : {numPages: number}) {
        setNumPages(numPages);
        setTermsPdfLoaded(true);
    }

    function getPageScaleFactor(){
        return clientWidth > 1600 ? 1.5 : 1;
    }

    useEffect(() => {
        const updateWindowDimensions = () => {
          const newWidth = window.innerWidth;
          setClientWidth(newWidth);
        };
    
        window.addEventListener('resize', updateWindowDimensions);
    
        return () => window.removeEventListener('resize', updateWindowDimensions);
    
    }, []);

    const getAgreementStartDate = () => {
        if(offer?.startDate){
            return new Date(offer?.startDate);
        }
    }

    const getAgreementEndDate = () => {
        if(offer?.endDate){
            return offer?.endDate;
        }

        if(selectedPackage.contractPeriod?.type === ContractPeriodType.TEMPORARY && selectedPackage.contractEndDate){
            return selectedPackage.contractEndDate
        }
    }

    const getAgreementMaxStartDate = () => {
        const maxDate = moment().add(MAX_DATE_DAYS, 'days').toDate();
        let endDate = offer?.endDate ?? selectedPackage.contractEndDate;
        if(!endDate) return maxDate;
        if(moment(endDate).isBefore(maxDate)) return moment(endDate).toDate();
        return maxDate;
    }

    const renderAgreementStartView = () => {
        if(!state.changePackageAgreement) {
            if(agreementStartDate) return (
                <>
                    <Form.Label className="mt-2">
                        <span className='text-nowrap'>Lepingu algus</span>
                    </Form.Label>
                    <span className="u-fs-16">
                        {moment(new Date(agreementStartDate)).format(dateFormat)}
                    </span>
                </>
            )

            return (
                <DatePicker
                    className={form.startDate === undefined && form.validated ? 'invalid' : ''}
                    value={agreementStartDate ?? form.startDate}
                    locale='et'
                    clearIcon={null}
                    calendarIcon={<CalendarIcon />}
                    format='dd.MM.yyyy'
                    minDate={earliestPossibleAgreementStartDate}
                    maxDate={maxContractStartDate}
                    onChange={(event: any) => {
                        setForm({
                            ...form, startDate: event ? new Date(event) : undefined
                        })
                    }}
                />
            )
        }

        if(isExistingAgreementStartInFuture) return (
            <div className="u-fs-16">{ moment(state.changePackageAgreement!.startDate).format(dateFormat) }</div>
        )

        return (
            <>
                { possibleDatesForPackageChange.map(date =>
                    <Form.Check
                        key={date.format(isoDateFormat)}
                        type="radio"
                        className="radio-large my-1"
                        custom
                        label={date.format(dateFormat)}
                        id={'radio-' + date.format(isoDateFormat)}
                        checked={form.startDate?.toISOString() === date.toDate().toISOString()}
                        onChange={() => setForm({...form, startDate: date.toDate()})}
                    />
                    )
                }
            </>
        )
    }

    const scaleFactor = getPageScaleFactor();
    const consumptionMarginal = offer?.consumptionMarginal ?? selectedPackage.marginal;
    const productionMarginal = (offer?.productionMarginal || state.changePackageAgreement?.productionMarginal || productionPackage?.marginal) ?? undefined;

    const basePriceCentsKwh = offer?.basePriceCentsKwh ?? selectedPackage.basePriceCentsKwh;
    const baseConsumptionProportion = offer?.baseConsumptionProportion ?? selectedPackage.baseConsumptionProportion;

    const dayTariffCentsKwh = offer?.dayTariffCentsKwh ?? selectedPackage.dayTariffCentsKwh;
    const nightTariffCentsKwh = offer?.nightTariffCentsKwh ?? selectedPackage.nightTariffCentsKwh;
    const monthlyFeeEuros = offer?.monthlyFeeEuros ?? selectedPackage.monthlyFeeEuros;
    const earlyTerminationFee = offer?.earlyTerminationFee ?? selectedPackage.earlyTerminationFee;

    const packageInfoLabelClassNames = 'col-4 col-sm-3 col-lg-6 col-xl-5 d-flex align-items-center text-gray-700 u-fs-14';
    const isVatLiable = isCompany && !!state.customer.vatRegistryNumber;
    const maxContractStartDate = getAgreementMaxStartDate();
    const agreementEndDate = getAgreementEndDate();
    const agreementStartDate = getAgreementStartDate();

    return (
        <div className="new-electricity-agreement__container">
            <h1>Vaadake üle andmed</h1>
            <FormContentBlock title="Klient">
                <NewElectricityAgreementCustomerForm
                    form={form}
                    setForm={setForm}
                    isCompany={isCompany}
                    isFormDisabled={false}
                    showConsolidatedInvoiceCheckbox={selectedMeteringPoints.length > 1}
                />
            </FormContentBlock>
            <FormContentBlock title="Arveldamine">
                <NewElectricityAgreementBillingForm
                    form={form}
                    setForm={setForm}
                />
            </FormContentBlock>
            <FormContentBlock title="Tarbimiskoht">
                <MeteringPointFormTable 
                    meteringPoints={selectedMeteringPoints} 
                    withResale={withResale}
                    isOffer={!!offer}
                />
            </FormContentBlock>
            <FormContentBlock title={withResale ? "Tarbimine": "Pakett"}>
                <div className="d-lg-flex justify-content-between align-items-center u-fs-16">
                    <div className="d-flex flex-column flex-1">
                        <div className="d-flex align-items-center mb-3">
                            <h2 className="mb-1">{ selectedPackage.nameEt }</h2>
                            { selectedPackage.greenEnergy ? <div className="new-electricity-agreement__package-card__green-energy ml-3">Roheteenus</div> : null }
                        </div>
                        <div className="row">
                            <div className="col-12 col-lg-8 col-xl-7">
                                {
                                    selectedPackage.twoTariffPackage ? (
                                        <>
                                            <div className="row">
                                                <div className={packageInfoLabelClassNames}>Päevahind:</div>
                                                <div className="col font-weight-bold">{ getPackagePrice(dayTariffCentsKwh, selectedPackage.basePriceTextEt, consumptionMarginal, isCompany) }</div>
                                            </div>
                                            <div className="row">
                                                <div className={packageInfoLabelClassNames}>Ööhind</div>
                                                <div className="col font-weight-bold">{ getPackagePrice(nightTariffCentsKwh, selectedPackage.basePriceTextEt, consumptionMarginal, isCompany) }</div>
                                        </div>
                                        </>
                                    ) : (
                                        <div className="row">
                                            <div className={packageInfoLabelClassNames}>{selectedPackage.dynamicPackage ? "Fikseeritud tarbimise hind:" : "Hind:"}</div>
                                            <div className="col font-weight-bold">{ getPackagePrice(basePriceCentsKwh, selectedPackage.basePriceTextEt, consumptionMarginal, isCompany) }</div>
                                        </div>
                                    )
                                }
                                {
                                    selectedPackage.dynamicPackage && (
                                        <div className="row">
                                            <div className={packageInfoLabelClassNames}>Fikseerimise tüüp:</div>
                                            <div className="col font-weight-bold"> {ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP[selectedPackage.baseFixationType!]} </div>
                                        </div>
                                    )
                                }
                                {
                                    selectedPackage.dynamicPackage !== null && isNotNullOrUndefined(baseConsumptionProportion) && (
                                        <div className="row">
                                            <div className={packageInfoLabelClassNames}>Fikseeritud tarbimise osakaal:</div>
                                            <div className="col font-weight-bold"> {roundNumber(baseConsumptionProportion, getBaseConsumptionProportionScale(selectedPackage.baseFixationType!))} {selectedPackage.baseFixationType === ElectricityPackageBaseFixationType.PERCENTAGE ? '%' : 'MWh'} </div>
                                        </div>
                                    )
                                }
                                <div className="row d-flex align-items-center">
                                    <div className={packageInfoLabelClassNames}>{selectedPackage.dynamicPackage ? "Börsihinnaga tarbimise marginaal:" : "Marginaal:"}</div>
                                    <div className="col font-weight-bold">{ isCompany ?  roundNumber(consumptionMarginal, 3) : getPriceWithVAT(consumptionMarginal, 3, moment()) } s/kWh</div>
                                </div>
                                <div className="row">
                                    <div className={packageInfoLabelClassNames}>Lepinguperiood:</div>
                                    <div className="col font-weight-bold">{ offer?.contractPeriod?.name || selectedPackage.contractPeriod?.name }</div>
                                </div>            
                                <div className="row">
                                    <div className={packageInfoLabelClassNames}>Kuutasu:</div>
                                    <div className="col font-weight-bold">{ (isCompany ? (monthlyFeeEuros ? roundNumber(monthlyFeeEuros, SUM_ROUNDING_SCALE) : 0) : getPriceWithVAT(monthlyFeeEuros || 0, SUM_ROUNDING_SCALE, moment())) } €</div>
                                </div>
                                { offer?.guarantee ? <div className="row">
                                    <div className={packageInfoLabelClassNames}>Tagatis:</div>
                                    <div className="col font-weight-bold">{ offer?.guarantee } €</div>
                                </div> : null }                                        
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col text-gray-700 u-fs-14">
                                { isCompany ? `Hindadele lisandub käibemaks` : `Hindades sisaldub käibemaks` }
                            </div>
                        </div>
                        {
                            offer?.consumptionPackage.earlyTerminationFeeType === ElectricityPackageEarlyTerminationFeeType.PERCENTAGE_OF_VOLUME && (
                                <>
                                    {
                                        offer.consumptionPackage.twoTariffPackage ? (
                                            <>
                                                <div className="row">
                                                    <p className="col mt-2 text-gray-700 u-fs-14">
                                                        Ennetähtaegse lepingu lõpetamise või paketi vahetamise korral rakendub ennetähtaegse
                                                        lõpetamise tasu. Tasu arvestatakse lepingu lõpuni jäänud perioodi kohta ning see 
                                                        arvutatakse järgmiselt: <span className="text-dark font-weight-bold">{offer.earlyTerminationFee}%</span> lepingu lõpuni jäänud arvestuslikust tarbimismahust, mis jaguneb päeva- ja öötarbimiseks, 
                                                        korrutatuna vastavate lepingus kokkulepitud päeva- ja ööhindadega hinnangulise tarbimise alusel kogu lepingu perioodi kohta ning on:
                                                    </p>
                                                </div> 
                                                <div className="row col-12 col-lg-8 col-xl-7 p-0">
                                                    <div className={packageInfoLabelClassNames}>Päevatarbimine:</div>
                                                    <div className="col font-weight-bold">{offer?.estimatedConsumptionDayVolumeMwh} MWh</div>
                                                </div>
                                                <div className="row col-12 col-lg-8 col-xl-7 p-0">
                                                    <div className={packageInfoLabelClassNames}>Öötarbimine:</div>
                                                    <div className="col font-weight-bold">{offer?.estimatedConsumptionNightVolumeMwh} MWh</div>
                                                </div>
                                                <div className="row col-12 col-lg-8 col-xl-7 p-0">
                                                    <div className={packageInfoLabelClassNames}>Kokku:</div>
                                                    <div className="col font-weight-bold">{offer?.estimatedConsumptionDayVolumeMwh! + offer?.estimatedConsumptionNightVolumeMwh!} MWh</div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="row">
                                                <p className="col mt-2 text-gray-700 u-fs-14">
                                                    Ennetähtaegse lepingu lõpetamise või paketi vahetamise korral rakendub ennetähtaegse
                                                    lõpetamise tasu. Tasu arvestatakse lepingu lõpuni jäänud perioodi kohta ning see
                                                    arvutatakse järgmiselt: <span className="text-dark font-weight-bold">{offer.earlyTerminationFee}%</span> lepingu lõpuni jäänud arvestuslik tarbimismaht korda lepingus
                                                    kokkulepitud ühiku hind. Hinnanguline tarbimismaht lepingu perioodi kohta: <span className="text-dark font-weight-bold">{offer.estimatedConsumptionVolumeMwh} MWh</span>.
                                                </p>
                                            </div>
                                        )
                                    }
                                </>
                            )
                        }
                    </div>
                    { !offer ? (
                        <Button 
                            variant="outline-primary" 
                            size="lg" 
                            className="stretchable-mobile-button mb-auto mt-2 mt-lg-0" 
                            onClick={onChangePackage}
                        >
                                Muuda paketti
                        </Button>
                    ) : null }
                </div>
            </FormContentBlock>
            {(withResale && !!productionMarginal) && (
                <FormContentBlock title="Tootmine">
                    <div className="d-flex justify-content-between align-items-center u-fs-16">
                        <div className="d-flex flex-column flex-1">
                            <div className="row">
                                <div className="col-12 col-lg-8 col-xl-7">
                                    <div className="row">
                                        <div className={packageInfoLabelClassNames}>Hind:</div>
                                        <div className="col font-weight-bold">{ productionPackage!.basePriceTextEt }</div>
                                    </div>
                                    <div className="row">
                                        <div className={packageInfoLabelClassNames}>Marginaal:</div>
                                        <div className="col font-weight-bold">
                                            { 
                                                roundNumber(productionMarginal, 3)
                                            } 
                                            s/kWh
                                        </div>
                                    </div>                                                           
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col text-gray-700 u-fs-14">
                                    { isVatLiable && `Lisandub käibemaks` }
                                </div>
                            </div>
                        </div>
                    </div>
                </FormContentBlock>
            )}
            <FormContentBlock title="Leping">
                <div className="d-lg-flex justify-content-between align-items-start">
                    <Form.Group controlId={'form-date'} className='d-flex flex-column'>
                        {
                            !agreementStartDate ? (
                                <Form.Label>
                                    <span className='text-nowrap'>{isExistingAgreementStartInFuture ? 'Lepingu algus' : 'Võimalik lepingu algus'}</span>
                                </Form.Label>
                            ) : null
                        }
                        { renderAgreementStartView() }
                        {
                            !agreementStartDate && form.startDate === undefined && form.validated &&
                                <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
                        }
                        {
                            agreementEndDate ? (
                                <>
                                    <Form.Label className="mt-2">
                                        <span className='text-nowrap'>Lepingu lõpp</span>
                                    </Form.Label>
                                    <span className="u-fs-16">
                                        {moment(new Date(agreementEndDate)).format(dateFormat)}
                                    </span>
                                </>
                            ) : null
                        }
                    </Form.Group>
                    <Button 
                        variant="outline-primary" 
                        size="lg" 
                        onClick={() => getPdfPreview(false)}
                        disabled={pdfPreviewLoading}
                        className="stretchable-mobile-button mt-2 mt-lg-0"
                    >
                        Vaata lepingut{ pdfPreviewLoading ? <Spinner size={"sm"} animation={"border"} className="ml-1"/> : null }
                    </Button>
                </div>
            </FormContentBlock>                

            <FormContentBlock title="Tingimused">
                <div className="new-electricity-agreement__summary__terms-pdf-container mt-1 mb-5">
                    { termsPdfLoaded ? null : <Spinner animation="grow" variant="primary" className="my-5" />}
                    <Document 
                        className="mx-n4 mx-lg-0" 
                        loading='' 
                        noData='' 
                        file={termsPdfPreview}
                        onLoadError={console.error} 
                        onLoadSuccess={onDocumentLoadSuccess}
                    >
                        {
                            Array(numPages).fill(0).map((_, i) => <Page key={i + ''} scale={scaleFactor}  pageNumber={i + 1}/>)
                        }
                    </Document>
                </div>
                <div className="col-12">
                    <Form.Check
                                id='check-terms'
                                custom
                                type="checkbox"
                                label={selectedPackage.confirmationText}
                                className='checkbox-large mt-3'
                                onChange={toggleTerms}
                                checked={termsAccepted}
                            />                      
                </div>
                <div className="col-12 mt-3">
                    <p>{selectedPackage.agreementWithdrawalConditions}</p>
                </div>
      
            </FormContentBlock>

            <PDFPreviewModal
                show={!!pdfPreview}
                onHide={() => setPdfPreview(undefined)}
                onDownload={() => getPdfPreview(true)}
                isDownloading={pdfPreviewIsDownloading}
                onPrint={() => printJS({printable: pdfPreview!, type: 'pdf'})}
                pdfPreview={pdfPreview}
            />
        </div>
    );
}

export const MeteringPointFormTable = ({ 
    meteringPoints, 
    withResale, 
    isOffer
}: { meteringPoints: MeteringPoint[], withResale: boolean, isOffer: boolean }) => {
    return (<table className="new-electricity-agreement__metering-point-table">
          <thead>
          <tr>
              <th className="w-25">EIC</th>
              <th className={withResale ? 'w-50' : 'w-75'}>AADRESS</th>
              {
                withResale && (
                  <th className="w-25">TOOTMISVÕIMSUS(kW)</th>
                )
              }
          </tr>
          </thead>
          <tbody>
          {
              meteringPoints.map((mp) => {
                const productionCapacity = isOffer ? 
                    mp.offerProductionCapacity : mp.productionCapacity;
                
                return (
                    <tr key={mp.eic}>
                        <td className="w-25">{mp.eic}</td>
                        <td className={withResale ? 'w-50' : 'w-75'}>{mp.address}</td>
                        {withResale && (
                            <td className="w-25">{isNaN(productionCapacity) ? "-" : productionCapacity}</td>
                        )}
                    </tr>
                )
              })
          }
          </tbody>
      </table>
    )
}